// "Tell me a rule" preview step, design §6 items 1-6: verbatim restatement +
// Edit instruction, human-readable resolved labels (never IDs), a truthful
// line-item match count, enriched sample rows, and kind-dependent save
// semantics (event trip context vs. a fixed merchant/keyword/account rule).
import { useState } from 'react';
import { Button, Checkbox, Label } from '@moneyskyline/ui';
import { AmountText } from '@/components/AmountText';
import { formatHumanDate } from '@/lib/date';
import { useCategories } from '@/api/hooks/useCategories';
import { useAccounts } from '@/api/hooks/useAccounts';
import { useTransaction } from '@/api/hooks/useTransaction';
import type { RuleDraft, RuleDraftSample } from '@/api/hooks/useRuleDraft';

const MAX_SAMPLES = 20;

interface ResolvedField {
  label: string;
  value: string | null;
  /** True when this field is present on the wire but could not be resolved to a human label. */
  unresolved: boolean;
  /** True while the lookup this field depends on (categories/accounts) is still in flight -- distinct from a confirmed failure to resolve (§3 loading-vs-failed). */
  loading: boolean;
}

function resolveFields(
  draft: RuleDraft,
  categoryName: (id: string) => string | null,
  accountName: (id: string) => string | null,
  categoriesLoading: boolean,
  accountsLoading: boolean,
): ResolvedField[] {
  const r = draft.resolved;
  const fields: ResolvedField[] = [];

  if (r.category_id !== null) {
    if (categoriesLoading) {
      fields.push({ label: 'Type', value: null, unresolved: false, loading: true });
    } else {
      const name = categoryName(r.category_id);
      fields.push({ label: 'Type', value: name, unresolved: name === null, loading: false });
    }
  }
  if (r.scope !== null) {
    // ENG Q3: the backend types `scope` as an open string. A recognised
    // value renders normally; anything else is this one field's "Details
    // unavailable" (Save disabled), not a collapse of the whole preview.
    const known = r.scope === 'personal' || r.scope === 'business';
    fields.push({
      label: 'Scope',
      value: known ? (r.scope === 'business' ? 'Business' : 'Personal') : null,
      unresolved: !known,
      loading: false,
    });
  }
  if (r.merchant_id !== null) {
    // Server-resolved (ENG-R1-007) so this never depends on a sample
    // transaction being loaded -- a placeholder here only when
    // `merchant_display` is absent; the component body fills it in from
    // enriched sample transactions as a fallback (§6 item 2).
    fields.push({ label: 'Merchant', value: r.merchant_display ?? null, unresolved: !r.merchant_display, loading: false });
  }
  if (r.account_id !== null) {
    if (accountsLoading) {
      fields.push({ label: 'Account', value: null, unresolved: false, loading: true });
    } else {
      const name = accountName(r.account_id);
      fields.push({ label: 'Account', value: name, unresolved: name === null, loading: false });
    }
  }
  if (r.start_date !== null) fields.push({ label: 'From', value: r.start_date, unresolved: false, loading: false });
  if (r.end_date !== null) fields.push({ label: 'Through', value: r.end_date, unresolved: false, loading: false });
  if (r.location !== null) fields.push({ label: 'Location', value: r.location, unresolved: false, loading: false });
  if (r.keyword !== null) fields.push({ label: 'Contains', value: r.keyword, unresolved: false, loading: false });

  return fields;
}

function SampleRow({ sample }: { sample: RuleDraftSample }) {
  const transaction = useTransaction(sample.transaction_id);

  if (transaction.isLoading) {
    return (
      <li className="flex items-center justify-between gap-2 py-1 text-xs text-muted-foreground" data-testid="rule-draft-sample-loading">
        Loading…
      </li>
    );
  }

  if (transaction.isError || !transaction.data) {
    return (
      <li className="flex items-center justify-between gap-2 py-1 text-xs" data-testid="rule-draft-sample-error">
        <span className="text-muted-foreground">Sample details unavailable</span>
        <Button type="button" size="sm" variant="ghost" onClick={() => transaction.refetch()}>
          Retry
        </Button>
      </li>
    );
  }

  const merchantName = transaction.data.merchant?.display_name ?? transaction.data.merchant_raw;
  return (
    <li className="flex items-center justify-between gap-3 py-1 text-xs" data-testid="rule-draft-sample-row">
      <span className="min-w-0 flex-1 truncate">
        {merchantName} · {formatHumanDate(transaction.data.posted_at)} · {transaction.data.account.display_name}
      </span>
      <AmountText value={sample.amount} currency={transaction.data.currency} variant="amount" />
    </li>
  );
}

/** Resolves the Merchant field's human name from the first sample transaction whose merchant matches the draft's `merchant_id`. */
function useMerchantFieldName(merchantId: string | null, samples: RuleDraftSample[]): { name: string | null; loading: boolean } {
  // Reuse whichever sample transaction is already loaded elsewhere in the
  // tree via SampleRow's own useTransaction call (react-query cache) -- this
  // hook only needs one lookup, the first sample, since that fetch is shared.
  const first = samples[0];
  const transaction = useTransaction(merchantId ? first?.transaction_id : undefined);
  if (!merchantId) return { name: null, loading: false };
  if (transaction.isLoading) return { name: null, loading: true };
  const matches = transaction.data?.merchant?.merchant_id === merchantId;
  return { name: matches ? (transaction.data?.merchant?.display_name ?? null) : null, loading: false };
}

export function RuleDraftPreview({
  draft,
  pending,
  onCommit,
  onDiscard,
  onEditInstruction,
  discardPending,
}: {
  draft: RuleDraft;
  pending?: boolean;
  onCommit: (hard: boolean) => void;
  onDiscard: () => void;
  onEditInstruction: () => void;
  discardPending?: boolean;
}) {
  const [hard, setHard] = useState(false);
  const categories = useCategories();
  const accounts = useAccounts();

  const categoryName = (id: string): string | null => {
    if (!categories.data) return null;
    return categories.data.categories.find((c) => c.category_id === id)?.name ?? null;
  };
  const accountName = (id: string): string | null => {
    if (!accounts.data) return null;
    return accounts.data.find((a) => a.account_id === id)?.display_name ?? null;
  };

  const samples = draft.samples.slice(0, MAX_SAMPLES);
  // ENG-R1-007: the server-resolved `merchant_display` is the primary
  // source; only fall back to the sample-derived lookup (and only pay for
  // its fetch) when the wire didn't carry a display name.
  const needsMerchantFallback = draft.resolved.merchant_id !== null && !draft.resolved.merchant_display;
  const merchantField = useMerchantFieldName(needsMerchantFallback ? draft.resolved.merchant_id : null, samples);

  const fields = resolveFields(draft, categoryName, accountName, categories.isLoading, accounts.isLoading).map((field) => {
    if (field.label !== 'Merchant' || !needsMerchantFallback) return field;
    return {
      ...field,
      unresolved: !merchantField.loading && merchantField.name === null,
      loading: merchantField.loading,
      value: merchantField.name,
    };
  });
  const hasUnresolvedField = fields.some((f) => f.unresolved);
  const hasLoadingField = fields.some((f) => f.loading);

  const kind = draft.resolved.kind;
  const isEvent = kind === 'event';
  const eventHasCategoryAction = isEvent && draft.resolved.category_id !== null;

  const primaryLabel = isEvent && !hard ? 'Save trip context' : 'Save rule';
  const saveDisabled = pending || hasUnresolvedField || hasLoadingField;

  return (
    <div className="mt-3 rounded-lg border border-border bg-card p-4 text-sm" data-testid="rule-draft-preview">
      <h3 className="text-title">Review before saving</h3>

      <div className="mt-2 flex items-start justify-between gap-3">
        <p className="whitespace-pre-wrap" data-testid="rule-draft-restatement">
          {draft.restatement}
        </p>
        <Button type="button" variant="link" size="sm" className="shrink-0" onClick={onEditInstruction}>
          Edit instruction
        </Button>
      </div>

      {hasUnresolvedField && (
        <p className="mt-2 rounded-md bg-degraded-surface p-2 text-xs text-degraded-foreground" role="alert" data-testid="rule-draft-details-unavailable">
          Details unavailable. Refresh the preview before saving.
        </p>
      )}
      {!hasUnresolvedField && hasLoadingField && (
        <p className="mt-2 rounded-md bg-muted p-2 text-xs text-muted-foreground" role="status" data-testid="rule-draft-details-loading">
          Loading details…
        </p>
      )}

      <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="rule-draft-resolved">
        {fields.map((field) => (
          <div key={field.label} className="flex gap-1">
            <dt className="font-medium">{field.label}:</dt>
            <dd>{field.loading ? 'Loading…' : field.unresolved ? '—' : field.value}</dd>
          </div>
        ))}
      </dl>

      <p className="mt-3 text-sm" data-testid="rule-draft-match-count">
        {draft.preview_count === 0
          ? 'No items match right now'
          : `${draft.preview_count} matching ${draft.preview_count === 1 ? 'item' : 'items'}`}
      </p>
      <p className="text-xs text-muted-foreground">Categories you set yourself stay as they are.</p>

      {samples.length > 0 && (
        <ul className="mt-2 flex flex-col divide-y divide-border" data-testid="rule-draft-samples">
          {samples.map((sample) => (
            <SampleRow key={sample.line_item_id} sample={sample} />
          ))}
        </ul>
      )}
      {samples.length > 0 && draft.preview_count > samples.length && (
        <p className="mt-1 text-xs text-muted-foreground">
          Showing {samples.length} of {draft.preview_count} matching items
        </p>
      )}

      {isEvent ? (
        eventHasCategoryAction ? (
          <div className="mt-3 flex items-start gap-2">
            <Checkbox id="rule-draft-hard" checked={hard} onCheckedChange={(checked) => setHard(checked === true)} />
            <div>
              <Label htmlFor="rule-draft-hard">Always apply this rule</Label>
              <p className="text-xs text-muted-foreground" data-testid="rule-draft-hard-helper">
                {hard
                  ? 'Apply the specified category to matching activity. Categories you set yourself stay as they are.'
                  : 'Use this trip as context when reviewing categories.'}
              </p>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="rule-draft-context-only">
            Use this trip as context when reviewing categories.
          </p>
        )
      ) : (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="rule-draft-fixed-rule-text">
          This saves a rule for matching activity.
        </p>
      )}

      <p className="mt-2 text-xs text-muted-foreground">
        Saving starts a category review across your transactions. Other matching rules can also apply.
      </p>

      <div className="mt-3 flex gap-2">
        <Button type="button" disabled={saveDisabled} onClick={() => onCommit(isEvent ? hard : false)}>
          {pending ? 'Saving…' : primaryLabel}
        </Button>
        <Button type="button" variant="outline" disabled={pending || discardPending} onClick={onDiscard}>
          {discardPending ? 'Discarding…' : 'Discard preview'}
        </Button>
      </div>
    </div>
  );
}

export default RuleDraftPreview;
